import { Link } from "react-router";
import { Loader2, RefreshCw, Zap } from "lucide-react";

import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import type {
  BidAnalysis,
  BidAnalysisRow,
  MyCompanyDocumentSlot,
  MyCompanyDocumentsState,
} from "@/types/domain";

/**
 * La cabecera de la pestaña de requisitos, en sus dos estados: sin documentos
 * cargados (una invitación a cargarlos) y con el cruce ya corrido (el veredicto
 * y cuántos puntos están en juego).
 */

/**
 * El checklist de documentos, en sus dos caras: lo que falta por cargar y lo que
 * ya está. Es la misma card — cambia el rótulo de cada casilla y el contador.
 */
function DocumentChecklist({
  documents,
}: {
  documents: MyCompanyDocumentsState | null;
}) {
  // Sin respuesta del backend todavía, se pintan las casillas vacías: el usuario
  // ve qué le vamos a pedir aunque la consulta no haya vuelto.
  const slots = documents?.slots ?? FALLBACK_SLOTS;
  const loaded = documents?.loadedCount ?? 0;

  return (
    <div className="w-full shrink-0 rounded-xl border bg-card p-4 lg:w-[34rem]">
      <div className="mb-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">
        {loaded > 0 ? "Documentos de tu empresa" : "Pendiente de carga"} ·{" "}
        {loaded} de {slots.length}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {slots.map((slot) => (
          <div
            key={slot.type}
            className="flex items-center justify-between gap-2 rounded-lg border bg-slate-50/60 px-3 py-2 dark:bg-slate-900/20"
          >
            <div className="min-w-0">
              <div className="truncate text-[13px] font-semibold text-slate-700 dark:text-slate-200">
                {slot.title}
              </div>
              <div
                className="truncate text-[11px] text-slate-400"
                title={slot.detail ?? undefined}
              >
                {slot.detail ?? slot.hint}
                {slot.updatedAt ? ` · ${formatDate(slot.updatedAt)}` : ""}
              </div>
            </div>
            <span
              className={cn(
                "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                slot.loaded
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400"
                  : "bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400",
              )}
            >
              {slot.loaded ? "Cargado" : "Falta"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Las casillas que se pintan mientras el backend responde. El orden, los
 * títulos y las pistas espejan `organizationdocuments.service.ts`; si cambian
 * allá, acá también.
 */
const emptySlot = (type: string, title: string, hint: string): MyCompanyDocumentSlot => ({
  type,
  title,
  hint,
  essential: false,
  loaded: false,
  state: "EMPTY",
  detail: null,
  updatedAt: null,
  documents: [],
});

const FALLBACK_SLOTS: MyCompanyDocumentSlot[] = [
  emptySlot("CONSTANCIA", "Constancias", "RNP y, si haces obras, tu capacidad de contratación"),
  emptySlot("EXPERIENCIA", "Experiencia", "Contratos con su conformidad o acta de recepción"),
  emptySlot("PLANTEL", "Personal clave", "CV, título y habilidad vigente"),
  emptySlot("CERTIFICACION", "Certificados ISO", "9001, 14001, 45001, 37001"),
  emptySlot("EQUIPAMIENTO", "Equipamiento", "Facturas, tarjetas de propiedad o alquiler"),
];

/**
 * La invitación a cargar documentos, cuando todavía no hay ninguno. Es el
 * estado que ve casi todo el mundo la primera vez.
 */
export function EmptyCompanyHero({
  totalScore,
  hardCount,
  documents,
}: {
  totalScore: number | null;
  hardCount: number;
  documents: MyCompanyDocumentsState | null;
}) {
  return (
    <div className="mt-6 flex flex-col gap-6 rounded-2xl border bg-slate-50/70 p-6 dark:bg-slate-900/20 lg:flex-row lg:items-center">
      <div className="flex min-w-0 flex-1 flex-col items-start justify-center">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-slate-200/70 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            Sin evaluar
          </span>
          <span className="text-sm text-slate-500">
            {totalScore !== null && `${totalScore} puntos en juego · `}
            {hardCount} requisitos obligatorios
          </span>
        </div>

        <h3 className="text-xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
          Carga los documentos de tu empresa para saber si calificas
        </h3>
        <p className="mt-1.5 max-w-lg text-sm leading-relaxed text-slate-500">
          Comparamos cada requisito del pliego contra tu RNP, tu experiencia,
          tu personal, tu equipamiento y tus certificados. Toma unos
          minutos y sirve para todas las licitaciones.
        </p>

        <Button className="mt-5" asChild>
          <Link to="/settings/documents">Subir documentos</Link>
        </Button>
      </div>

      <DocumentChecklist documents={documents} />
    </div>
  );
}

/**
 * Hay documentos pero nadie ha corrido el cruce todavía. El cruce cuesta una
 * llamada al modelo, así que no se dispara solo: lo pide el usuario cuando le
 * interesa esta licitación en particular.
 */
export function ReadyToAnalyzeHero({
  totalScore,
  requirementCount,
  documents,
  onRun,
  isRunning,
}: {
  totalScore: number | null;
  requirementCount: number;
  documents: MyCompanyDocumentsState | null;
  onRun: () => void;
  isRunning: boolean;
}) {
  return (
    <div className="mt-6 flex flex-col gap-6 rounded-2xl border border-primary/20 bg-primary/5 p-6 lg:flex-row lg:items-center">
      <div className="flex min-w-0 flex-1 flex-col items-start justify-center">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-primary-foreground">
            Listo para evaluar
          </span>
          <span className="text-sm text-slate-500">
            {documents
              ? `${documents.loadedCount} de ${documents.totalCount} documentos cargados · `
              : ""}
            {totalScore !== null && `${totalScore} puntos en juego`}
          </span>
        </div>

        <h3 className="text-xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
          Cruza tus documentos con los {requirementCount} requisitos de esta
          licitación
        </h3>
        <p className="mt-1.5 max-w-lg text-sm leading-relaxed text-slate-500">
          El cruce lee el pliego y lo compara con tu RNP, tu experiencia, tu personal
          y tus certificados. Tarda alrededor de un minuto y puedes repetirlo
          cuando actualices un documento.
        </p>

        <Button className="mt-5" onClick={onRun} disabled={isRunning}>
          {isRunning ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Cruzando…
            </>
          ) : (
            <>
              <Zap className="size-4" />
              Cruzar documentos ahora
            </>
          )}
        </Button>
      </div>

      <DocumentChecklist documents={documents} />
    </div>
  );
}

const VERDICTS = {
  CALIFICA: {
    label: "Califica",
    card: "border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/50 dark:bg-emerald-950/20",
    pill: "bg-emerald-600 text-white",
  },
  REVISAR: {
    label: "Revisar",
    card: "border-amber-200 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20",
    pill: "bg-amber-500 text-white",
  },
  NO_CALIFICA: {
    label: "No califica",
    card: "border-rose-200 bg-rose-50/60 dark:border-rose-900/50 dark:bg-rose-950/20",
    pill: "bg-rose-600 text-white",
  },
} as const;

/**
 * Los tres montones en los que cae cada requisito, y por qué importan por
 * separado: lo acreditado ya está, lo que se compromete se resuelve firmando en
 * la oferta, y lo que queda por revisar es lo único que exige hacer algo.
 */
export const OUTCOMES = [
  {
    key: "cumple" as const,
    label: "Cumple",
    chip: "Cumple",
    hint: "acreditado hoy",
    statuses: ["CUMPLE", "CUMPLE_CON_OBSERVACION"],
    tone: "text-emerald-700 dark:text-emerald-400",
    pill: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300",
  },
  {
    key: "compromete" as const,
    label: "Se compromete",
    chip: "Se compromete",
    hint: "carta en la oferta",
    statuses: ["SE_COMPROMETE"],
    tone: "text-blue-700 dark:text-blue-400",
    pill: "bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300",
  },
  {
    key: "cerrar" as const,
    label: "Por revisar",
    /** En el filtro se cae el "por": es más corto y ya va junto a los otros. */
    chip: "Revisar",
    hint: "falta resolverlo",
    statuses: ["NO_CUMPLE", "REVISAR"],
    tone: "text-orange-700 dark:text-orange-400",
    pill: "bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-300",
  },
];

export const countByOutcome = (rows: BidAnalysisRow[], key: string) =>
  rows.filter((row) =>
    OUTCOMES.find((o) => o.key === key)?.statuses.includes(row.estado),
  ).length;

/**
 * El veredicto del cruce: si califica, con cuántos puntos y en qué estado. Se
 * puede rehacer siempre, y lo pide a gritos cuando la ficha cambió después.
 */
export function AnalysisHero({
  analysis,
  onRerun,
  isRunning,
}: {
  analysis: BidAnalysis;
  onRerun: () => void;
  isRunning: boolean;
}) {
  const verdict = VERDICTS[analysis.verdict] ?? VERDICTS.REVISAR;
  const points = analysis.points;

  const hard = analysis.rows.filter(
    (row) => row.categoria === "DURO" && row.estado !== "NO_APLICA",
  );
  const hardPassing = hard.filter(
    (row) => row.estado !== "NO_CUMPLE" && row.estado !== "REVISAR",
  ).length;

  // Lo que tiene más lo que está a su alcance. Es la cifra que decide si vale la
  // pena postular: los puntos de hoy solos subestiman a quien puede
  // comprometerse en la oferta.
  const projected = points ? points.acreditado + points.alcanzable : null;

  return (
    <div
      className={cn(
        "mt-6 flex flex-col gap-6 rounded-2xl border p-6 lg:flex-row lg:items-center",
        verdict.card,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <span
            className={cn(
              "rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider",
              verdict.pill,
            )}
          >
            {verdict.label}
          </span>
          <span className="text-sm text-slate-600 dark:text-slate-300">
            Tu empresa cumple {hardPassing} de {hard.length} requisitos
            obligatorios
          </span>
        </div>

        {analysis.isStale && (
          <p className="mb-3 max-w-lg rounded-lg bg-amber-100/70 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            Tus documentos cambiaron después de este cruce. Rehazlo para ver el
            resultado con tu ficha actual.
          </p>
        )}

        {points && projected !== null && points.disponible > 0 && (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-extrabold tracking-tight text-slate-800 dark:text-slate-100">
                {projected}
              </span>
              <span className="text-sm text-slate-500">
                de {points.disponible} puntos proyectados
              </span>
            </div>

            <div className="mt-3 flex h-2 w-full max-w-lg overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
              <div
                className="bg-emerald-700"
                style={{
                  width: `${(points.acreditado / points.disponible) * 100}%`,
                }}
              />
              <div
                className="bg-emerald-400"
                style={{
                  width: `${(points.alcanzable / points.disponible) * 100}%`,
                }}
              />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-emerald-700" />
                {points.acreditado} ya acreditados
              </span>
              <span className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-emerald-400" />
                {points.alcanzable} a su alcance con compromisos
              </span>
              {points.valla !== null && (
                <span>Valla técnica: {points.valla}</span>
              )}
            </div>
          </>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            variant={analysis.isStale ? "default" : "outline"}
            onClick={onRerun}
            disabled={isRunning}
          >
            {isRunning ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            {isRunning ? "Cruzando…" : "Rehacer cruce"}
          </Button>
          <span className="text-xs text-slate-400">
            Cruzado el {formatDate(analysis.updatedAt)}
          </span>
        </div>
      </div>

      <div className="grid w-full shrink-0 grid-cols-1 gap-3 sm:grid-cols-3 lg:w-[34rem]">
        {OUTCOMES.map((outcome) => (
          <div
            key={outcome.key}
            className="rounded-xl border bg-card px-4 py-3"
          >
            <div className={cn("text-2xl font-extrabold", outcome.tone)}>
              {countByOutcome(analysis.rows, outcome.key)}
            </div>
            <div
              className={cn(
                "text-[11px] font-bold uppercase tracking-wider",
                outcome.tone,
              )}
            >
              {outcome.label}
            </div>
            <div className="text-xs text-slate-400">{outcome.hint}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
